import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { Asset, Document } from "../types/database";
import Modal from "../components/Modal";
import AddAssetForm from "../components/AddAssetForm";
import EditInventoryItemForm from "../components/EditInventoryItemForm";
import DocumentForm from "../components/DocumentForm";
import UserName from "../components/UserName";

// Helper function to format currency
function formatGBP(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

// Helper function to format date
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function InventoryDetail() {
  const { id, orgId } = useParams<{ id: string; orgId: string }>();
  const { currentOrganization } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "details" | "assets" | "documents"
  >("details");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Modals state
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState(false);
  const [isEditItemModalOpen, setIsEditItemModalOpen] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);

  const {
    data: item,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["inventory", id, currentOrganization?.id],
    queryFn: () => {
      if (!id) throw new Error("Inventory item ID is required");
      return inventoryService.getInventoryItem(id, currentOrganization?.id);
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading item details...</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Item Not Found
        </h2>
        <p className="text-gray-600 mb-6">
          The inventory item you're looking for doesn't exist or has been
          removed.
        </p>
        <Link
          to="/inventory"
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          Return to Inventory
        </Link>
      </div>
    );
  }

  const handleAddAssetSuccess = () => {
    setIsAddAssetModalOpen(false);
    refetch();
  };

  const handleEditItemSuccess = () => {
    setIsEditItemModalOpen(false);
    refetch();
  };

  const openEditDocumentModal = (document: Document) => {
    setEditingDocument(document);
    setIsAddDocumentModalOpen(true);
  };

  // Pagination logic for assets
  const assets = item.assets || [];
  const totalAssets = assets.length;
  const totalPages = Math.ceil(totalAssets / itemsPerPage);
  const indexOfLastAsset = currentPage * itemsPerPage;
  const indexOfFirstAsset = indexOfLastAsset - itemsPerPage;
  const currentAssets = assets.slice(indexOfFirstAsset, indexOfLastAsset);

  // Pagination controls
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return (
    <div className="space-y-6">
      {/* Header with back button and actions */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Link
            to={`/org/${orgId}/inventory`}
            className="text-gray-600 hover:text-primary transition-colors"
            aria-label="Back to inventory"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold">{item.name}</h1>
          <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-800">
            {item.category}
          </span>
        </div>

        <div className="flex gap-2">
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            onClick={() => setIsEditItemModalOpen(true)}
          >
            Edit Item
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Image and availability */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Main image */}
            <div className="relative">
              <img
                src={
                  item.images[currentImageIndex] ||
                  "https://placehold.co/600x400?text=NO+IMAGE"
                }
                alt={item.name}
                className="w-full h-64 object-contain p-4 cursor-pointer"
                onClick={() => setIsFullScreen(true)}
              />
            </div>

            {/* Thumbnail gallery */}
            {item.images.length > 1 && (
              <div className="p-4 border-t border-gray-100">
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {item.images.map((image: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        index === currentImageIndex
                          ? "border-primary"
                          : "border-transparent hover:border-gray-200"
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${item.name} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Availability section */}
            <div className="p-4 border-t border-gray-100">
              <h3 className="font-medium text-lg mb-3">Availability</h3>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold">
                    {item.assets?.length || 0}
                  </div>
                  <div className="text-sm text-gray-500">Total</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {item.assets?.filter((a: Asset) => a.status === "available")
                      .length || 0}
                  </div>
                  <div className="text-sm text-gray-500">Available</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary">
                    {item.assets?.filter(
                      (a: Asset) => a.status === "checked_out"
                    ).length || 0}
                  </div>
                  <div className="text-sm text-gray-500">Out</div>
                </div>
              </div>

              {/* <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Location:</span>
                  <span className="text-sm">{item.location}</span>
                </div>
                {item.dry_hire_price && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Dry Hire Price:</span>
                    <span className="text-sm font-bold text-primary">
                      {formatGBP(item.dry_hire_price)}/day
                    </span>
                  </div>
                )}
              </div> */}
            </div>
          </div>
        </div>

        {/* Right column - Details */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === "details"
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === "assets"
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("assets")}
              >
                Asset List
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm ${
                  activeTab === "documents"
                    ? "border-b-2 border-primary text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("documents")}
              >
                Documents
              </button>
            </div>

            <div className="p-6">
              {activeTab === "details" && (
                <div className="space-y-6">
                  {/* Item Details */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Item Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Category</span>
                        <span className="font-medium">{item.category}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Manufacturer</span>
                        <span className="font-medium">
                          {item.manufacturer || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Model</span>
                        <span className="font-medium">
                          {item.model || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">SKU</span>
                        <span className="font-medium">{item.sku || "N/A"}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Purchase Price</span>
                        <span className="font-medium">
                          {item.purchase_price
                            ? formatGBP(item.purchase_price)
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Rental Price</span>
                        <span className="font-medium">
                          {item.rental_price
                            ? formatGBP(item.rental_price)
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Asset Summary */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Asset Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg shadow-sm p-4">
                        <p className="text-sm font-medium text-gray-600">
                          Total Assets
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-gray-900">
                          {item.assets?.length || 0}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm p-4">
                        <p className="text-sm font-medium text-gray-600">
                          Total Value
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-gray-900">
                          {formatGBP(
                            item.purchase_price * (item.assets?.length || 0)
                          )}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          Based on purchase price
                        </p>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm p-4">
                        <p className="text-sm font-medium text-gray-600">
                          Available
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-green-600">
                          {item.assets?.filter(
                            (a: Asset) => a.status === "available"
                          ).length || 0}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm p-4">
                        <p className="text-sm font-medium text-gray-600">
                          Checked Out
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-primary">
                          {item.assets?.filter(
                            (a: Asset) => a.status === "checked_out"
                          ).length || 0}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          {Math.round(
                            ((item.assets?.filter(
                              (a: Asset) => a.status === "checked_out"
                            ).length || 0) /
                              (item.assets?.length || 1)) *
                              100
                          )}
                          % utilization
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {item.description && (
                    <div>
                      <h3 className="text-lg font-medium mb-2">Description</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700">{item.description}</p>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {item.notes && (
                    <div>
                      <h3 className="text-lg font-medium mb-2">Notes</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700">{item.notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Creation and Update Info */}
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <div>
                        <p className="text-sm text-gray-500">Created By</p>
                        <p className="font-medium">
                          <UserName userId={item.created_by} />
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDate(item.created_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Last Updated By</p>
                        <p className="font-medium">
                          <UserName userId={item.updated_by} />
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDate(item.updated_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "assets" && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Asset List</h3>
                    <div className="flex gap-2">
                      <button
                        className="px-3 py-1 bg-primary text-white text-sm rounded-md hover:bg-primary/90"
                        onClick={() => setIsAddAssetModalOpen(true)}
                      >
                        Add Asset
                      </button>
                      <button className="text-sm text-primary hover:underline">
                        Export Asset List
                      </button>
                    </div>
                  </div>
                  {item.assets?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Barcode
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Notes
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {currentAssets.map((asset: Asset) => (
                            <tr key={asset.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <Link
                                  to={`/org/${orgId}/asset/${asset.id}`}
                                  className="font-medium text-primary hover:underline"
                                >
                                  {asset.barcode}
                                </Link>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`inline-block px-2 py-1 text-xs rounded-full ${
                                    asset.status === "checked_out"
                                      ? "bg-primary/10 text-primary"
                                      : asset.status === "available"
                                      ? "bg-green-100 text-green-800"
                                      : asset.status === "maintenance"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {asset.status
                                    .replace("_", " ")
                                    .charAt(0)
                                    .toUpperCase() +
                                    asset.status.slice(1).replace("_", " ")}
                                </span>
                              </td>
                              <td className="px-4 py-3 max-w-32 truncate">
                                {asset.notes ? (
                                  <span
                                    className="cursor-help"
                                    title={asset.notes}
                                  >
                                    {asset.notes}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                <Link
                                  to={`/org/${orgId}/asset/${asset.id}`}
                                  className="text-sm text-primary hover:underline"
                                >
                                  View Details
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-4">
                        No assets available for this item
                      </p>
                      <button
                        onClick={() => setIsAddAssetModalOpen(true)}
                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                      >
                        Add Your First Asset
                      </button>
                    </div>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-100">
                      <div className="flex items-center text-sm text-gray-700">
                        <span>Items per page:</span>
                        <select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded-md"
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span className="mx-1">of</span>
                        <span>{totalAssets}</span>
                      </div>
                      <div className="flex items-center">
                        <button
                          onClick={prevPage}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-gray-600 hover:text-gray-900 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="mx-2 text-gray-700">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={nextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-gray-600 hover:text-gray-900 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "documents" && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Documents</h3>
                    <button
                      className="px-3 py-1 bg-primary text-white text-sm rounded-md hover:bg-primary/90"
                      onClick={() => setIsAddDocumentModalOpen(true)}
                    >
                      Add Document
                    </button>
                  </div>

                  {item.documents?.length > 0 ? (
                    <div className="space-y-3">
                      {item.documents.map((doc: Document) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center mr-3">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 text-gray-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                            <div>
                              <div className="text-sm font-medium">
                                {doc.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {doc.type.charAt(0).toUpperCase() +
                                  doc.type.slice(1)}
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 text-xs text-primary border border-primary rounded hover:bg-primary/5"
                            >
                              View
                            </a>
                            <button
                              onClick={() => openEditDocumentModal(doc)}
                              className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-4">
                        No documents available for this item
                      </p>
                      <button
                        onClick={() => setIsAddDocumentModalOpen(true)}
                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                      >
                        Add Your First Document
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full screen image modal */}
      {isFullScreen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
          <button
            onClick={() => setIsFullScreen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <div className="relative max-w-7xl mx-auto p-4">
            <img
              src={item.images[currentImageIndex]}
              alt={item.name}
              className="max-h-[90vh] w-auto mx-auto"
            />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
              {item.images.map((_: string, index: number) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2 h-2 rounded-full ${
                    index === currentImageIndex ? "bg-white" : "bg-gray-500"
                  }`}
                  aria-label={`View image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Asset Modal */}
      <Modal
        isOpen={isAddAssetModalOpen}
        onClose={() => setIsAddAssetModalOpen(false)}
        title="Add Asset"
        size="lg"
      >
        <AddAssetForm
          inventoryItemId={item.id}
          onSuccess={handleAddAssetSuccess}
          onCancel={() => setIsAddAssetModalOpen(false)}
        />
      </Modal>

      {/* Edit Item Modal */}
      <Modal
        isOpen={isEditItemModalOpen}
        onClose={() => setIsEditItemModalOpen(false)}
        title="Edit Inventory Item"
        size="lg"
      >
        <EditInventoryItemForm
          item={item}
          onSuccess={handleEditItemSuccess}
          onCancel={() => setIsEditItemModalOpen(false)}
        />
      </Modal>

      {/* Add/Edit Document Modal */}
      <Modal
        isOpen={isAddDocumentModalOpen}
        onClose={() => {
          setIsAddDocumentModalOpen(false);
          setEditingDocument(null);
        }}
        title={editingDocument ? "Edit Document" : "Add Document"}
        size="md"
      >
        <DocumentForm
          inventoryItemId={item.id}
          document={editingDocument || undefined}
          onSuccess={() => {
            setIsAddDocumentModalOpen(false);
            setEditingDocument(null);
            refetch();
          }}
          onCancel={() => {
            setIsAddDocumentModalOpen(false);
            setEditingDocument(null);
          }}
        />
      </Modal>
    </div>
  );
}
